<script setup lang="ts">
import type { ApalumaChatThread } from '@/types/api-response/chat-session-types.ts';
import { Icon } from '@iconify/vue';
import { ref, watch } from 'vue';
import { IconLib } from '@/assets/icon-library.ts';
import GradientButton from '@/components/button/gradient-button.vue';
import TextInput from '@/components/input/text-input.vue';

const props = defineProps<{
   session: ApalumaChatThread | null;
   error?: string | null;
}>();

const emit = defineEmits<{
   rename: [title: string];
}>();

const modelValue = defineModel<boolean>({ required: true, default: false });
const isRenaming = ref(false);
const newTitle = ref('');

// Reset form when modal opens/closes
watch(modelValue, (isOpen) => {
   if (isOpen && props.session) {
      newTitle.value = props.session.title;
      isRenaming.value = false;
   }
   else if (!isOpen) {
      isRenaming.value = false;
      newTitle.value = '';
   }
});

// Reset loading state when an error occurs
watch(() => props.error, (error) => {
   if (error) {
      isRenaming.value = false;
   }
});

async function handleRename() {
   if (!newTitle.value.trim()) {
      return;
   }

   isRenaming.value = true;
   emit('rename', newTitle.value.trim());
}

function handleCancel() {
   if (!isRenaming.value) {
      modelValue.value = false;
   }
}

function handleBackdropClick(e: MouseEvent) {
   if (e.target === e.currentTarget && !isRenaming.value) {
      handleCancel();
   }
}
</script>

<template>
   <Teleport to="body">
      <Transition
         enter-active-class="duration-300 ease-in"
         enter-from-class="opacity-0"
         enter-to-class="opacity-100"
         leave-active-class="duration-200 ease-in"
         leave-from-class="opacity-100"
         leave-to-class="opacity-0"
      >
         <div
            v-if="modelValue"
            class="fixed inset-0 z-[9999]"
         >
            <!-- Backdrop -->
            <div class="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm" @click="handleBackdropClick" />

            <!-- Modal Content -->
            <div class="flex min-h-full items-center justify-center p-4">
               <Transition
                  enter-active-class="duration-300 ease-out"
                  enter-from-class="opacity-0 scale-95"
                  enter-to-class="opacity-100 scale-100"
                  leave-active-class="duration-200 ease-in"
                  leave-from-class="opacity-100 scale-100"
                  leave-to-class="opacity-0 scale-95"
               >
                  <div
                     v-if="modelValue"
                     class="relative w-full max-w-xl transform overflow-hidden rounded-2xl bg-white shadow-xl px-6 py-8 border-2 border-black"
                     @click.stop
                  >
                     <!-- Close button -->
                     <button
                        class="absolute top-4 right-4 size-8 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        :disabled="isRenaming"
                        aria-label="Close modal"
                        @click="handleCancel"
                     >
                        <Icon :icon="IconLib.actions.close" class="size-5 text-gray-600" />
                     </button>

                     <h1 class="font-semibold text-3xl text-center">
                        Rename Chat
                     </h1>

                     <!-- Input field -->
                     <div class="mt-6 px-4">
                        <TextInput
                           v-model="newTitle"
                           type="text"
                           placeholder="New title"
                           :disabled="isRenaming"
                           @keyup.enter="handleRename"
                           @keyup.escape="handleCancel"
                        >
                        </TextInput>
                     </div>

                     <!-- Error message -->
                     <div
                        v-if="error"
                        class="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg"
                     >
                        <div class="flex items-center gap-2 text-red-600">
                           <Icon :icon="IconLib.error.circle" class="size-5 flex-shrink-0" />
                           <p class="text-sm font-medium">
                              {{ error }}
                           </p>
                        </div>
                     </div>

                     <div class="flex items-center justify-center gap-4 mt-6">
                        <GradientButton
                           variant="solid"
                           stretch="lg"
                           :disabled="isRenaming || !newTitle.trim()"
                           @click="handleRename"
                        >
                           <div class="flex items-center gap-2">
                              <Icon
                                 v-if="isRenaming"
                                 :icon="IconLib.loading.spinner"
                                 class="size-4 animate-spin"
                              />
                              <span>{{ isRenaming ? 'Saving...' : 'Save' }}</span>
                           </div>
                        </GradientButton>
                     </div>
                  </div>
               </Transition>
            </div>
         </div>
      </Transition>
   </Teleport>
</template>

<style scoped>

</style>
